<template>
  <div class="h-full overflow-hidden">
    <BaseTable
      table-id="modifiedRangeCustomerTradersites"
      :headers="headers"
      :list="modifiedTableData"
      :total-records="tableDataCount"
      :is-add-new-hidden="mode === 'view'"
      :hide-multiselect-delete="mode === 'view'"
      :selected-page="selectedPage"
      :items-per-page="itemsPerPage"
      hide-grouping
      isSavingLayoutHidden
      :edit-mode="mode === 'edit'"
      :is-add-all-button-active="false"
      @add-new="onAddTradersiteCustomers"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData($event)">
      <template #actions="{ item }: any">
        <div
          class="flex items-center justify-center gap-1"
          v-if="item.isFromManager && mode === 'edit'">
          <ActionButton
            :is-enabled="!item.deleted"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as RangeCustomerTradersite)" />
        </div>
      </template>
    </BaseTable>
    <RangeTradersitesModalAdd
      v-if="isModalAddActive"
      :list="traderSiteCustomersList"
      :total-records="traderSiteCustomersListCount"
      @multi-select="onTradersiteCustomerSelect"
      @refresh-data="onAddTradersiteCustomers"
      @close="isModalAddActive = false">
    </RangeTradersitesModalAdd>
  </div>
  <BaseModalConfirm
    v-if="deleteRowItem && modalDeleteRow"
    @cancel="modalDeleteRow = false"
    @confirm="onDeleteSingle">
    <template #title>{{ $t('common.actions.delete') }}</template>
    <template #text>
      {{
        $t('messages.info.deleteSingle', {
          'item': deleteRowItem?.traderSite?.name,
        })
      }}
    </template>
    <template #buttonText>{{ $t('common.actions.delete') }}</template>
  </BaseModalConfirm>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { deepCopy } from '@/utils/data';
import { v4 as uuidv4 } from 'uuid';
import ActionButton from '../common/ActionButton.vue';
import BaseTable from '../common/BaseTable.vue';
import RangeTradersitesModalAdd from './RangeTradersitesModalAdd.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { TraderSiteWithBaseCustomer } from '@/types/Traders';
import type { RangeCustomerTradersite } from '@/types/Ranges';

import { getTraderSitesWithCustomers } from '@/services/api/traders';
import { useToastStore } from '@/stores/toast';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();

const props = defineProps<{
  mode: 'view' | 'edit';
  tableData: RangeCustomerTradersite[];
  tableDataCount: number;
  selectedPage: number;
  itemsPerPage: number;
  rangeId: string;
  addedDetails: RangeCustomerTradersite[];
}>();

const emit = defineEmits(['add', 'refreshTableData', 'delete']);

const headers = computed(() => [
  {
    value: 'customer.code',
    text: $t('ranges.customerCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'traderSite.code',
    text: $t('ranges.traderSiteCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'traderSite.name',
    text: $t('ranges.traderSite'),
    type: 'text',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: props.mode == 'edit' },
]);

const modifiedTableData = ref(deepCopy<RangeCustomerTradersite[]>(props.tableData));
const isModalAddActive = ref(false);
const traderSiteCustomersList = ref<TraderSiteWithBaseCustomer[]>([]);
const traderSiteCustomersListCount = ref<number>();

watch(
  () => props.tableData,
  (newData) => {
    modifiedTableData.value = deepCopy<RangeCustomerTradersite[]>(newData);
  },
  { deep: true }
);

async function loadData(data: TableFilterData) {
  const sortColumn = data && data.column !== undefined ? headers.value[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
  };

  emit('refreshTableData', {
    ...params,
    rangeId: props.rangeId,
  });
}

async function onAddTradersiteCustomers(data: TableFilterData | undefined) {
  traderSiteCustomersList.value = [];
  const res = await getTraderSitesWithCustomers({
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || 'Tradersite.Name',
    sortAscending: data?.order === 'asc',
    excludePairs: props.tableData.map((x) => `${x.traderSiteId}|${x.customerId}`),
    rangeId: props.rangeId,
  });

  if (res) {
    traderSiteCustomersList.value = res.data;
    traderSiteCustomersListCount.value = res.count;
  }
  isModalAddActive.value = true;
}

function onTradersiteCustomerSelect(items: TraderSiteWithBaseCustomer[]) {
  isModalAddActive.value = false;
  const rangeTradersiteCustomers: RangeCustomerTradersite[] = [];
  items.forEach((item) => {
    const newItem: RangeCustomerTradersite = {
      id: uuidv4(),
      isFromManager: true,
      isActive: true,
      rangeId: props.rangeId,
      traderSiteId: item.traderSite.id,
      customerId: item.customer.id,
      hasDynamicRange: false,
      customer: {
        id: item.customer.id,
        code: item.customer.code || '',
      },
      traderSite: {
        id: item.traderSite.id,
        code: item.traderSite.code || '',
        name: item.traderSite.name || '',
      },
      recordMode: RecordMode.Added,
    };
    rangeTradersiteCustomers.push(newItem);
  });
  if (rangeTradersiteCustomers.length) emit('add', rangeTradersiteCustomers);
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<RangeCustomerTradersite>();

function onDeleteRow(item: RangeCustomerTradersite) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onDeleteMultiselect(items: RangeCustomerTradersite[]) {
  const itemsToDelete = items.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  emit('delete', itemsToDelete);
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;
  modalDeleteRow.value = false;
  emit('delete', [deleteRowItem.value]);
}
</script>
